import React, { useState, useEffect } from "react";

import Tippy from "@tippyjs/react";
import { Link, useHistory } from "react-router-dom";

import useClickOutside from "../../utils/CustomHooks/useClickOutside";
import { getRoles } from "../../utils/OktaHelpers";
import { LABSTORE_ACCESS, ROLES } from "../../utils/constants";
import { isAuthorized, isLabstoreAuthorized } from "../../utils/RolesHelpers";
import { t } from "i18next";
import { loginSubscriber } from "../../utils/Observer/ObserverService";

const SideMenu = ({ currentUser }) => {
    const [sideMenuOpen, setSideMenuOpen] = useState(false);
    const [fullMenu, setFullMenu] = useState(true);
    const [roles, setRoles] = useState([]);
    const [collapseTabs, setCollapseTabs] = useState({
        options: true,
        dashboard: true,
    });
    const [user, setUser] = useState({});
    const history = useHistory();

    useEffect(() => {
        if (currentUser) {
            //Runs once then only on loginSubscriber change
            loginSubscriber.subscribe(() => {
                setRoles(getRoles());
                setUser(currentUser);
            });
        }
    }, [currentUser]);

    const menuRef = useClickOutside(() => {
        setSideMenuOpen(false);
    });

    const toggleSideMenu = () => {
        setSideMenuOpen(!sideMenuOpen);
    };

    const isActive = (path) => {
        if (window.location.href.includes(path)) {
            return "active";
        }
        return "";
    };

    const closeSideMenu = (path) => {
        history.push(path);
        setSideMenuOpen(false);
    };

    const toggleFullMenu = () => {
        setFullMenu(!fullMenu);
    };

    const topTabs = [
        {
            accessor: "dashboard",
            name: t("dashboard"),
            icon: "home",
            subTabs: [
                {
                    name: t("overview"),
                    icon: "chart-line",
                    path: "/dashboard",
                },
                {
                    name: t("analytics"),
                    icon: "chart-pie",
                    path: "/analytics",
                },
            ],
        },
        {
            name: t("labStore"),
            icon: "store",
            path: "/store",
            hide: !isLabstoreAuthorized(user, LABSTORE_ACCESS.view),
            roles: [ROLES.partnerAdmin, ROLES.support],
        },
        {
            name: t("subscribedProducts"),
            icon: "cubes",
            path: "/subscribed-products",
            roles: [ROLES.partnerAdmin, ROLES.support, ROLES.admin],
            hide: !isLabstoreAuthorized(user, LABSTORE_ACCESS.subscribe),
            tippyContent: t("subscribedModelsTippy"),
        },
        {
            name: t("measurements"),
            icon: "chart-bar",
            path: "/measurements",
            roles: [
                ROLES.admin,
                ROLES.partnerUser,
                ROLES.partnerAdmin,
                ROLES.superAdmin,
                ROLES.support,
            ],
            tippyContent: t("measurementsTippy"),
        },
        {
            name: t("models"),
            icon: "atom",
            path: "/models",
            tippyContent: t("modelsTippy"),
        },
        {
            name: t("scans"),
            icon: "flask",
            path: "/scans",
            tippyContent: t("scansTippy"),
        },
        {
            name: t("affiliates"),
            icon: "building",
            path: "/affiliates",
            roles: [ROLES.partnerAdmin, ROLES.superAdmin, ROLES.support],
        },
        {
            name: t("headerUser"),
            icon: "user-friends",
            path: "/users",
            roles: [
                ROLES.admin,
                ROLES.partnerAdmin,
                ROLES.superAdmin,
                ROLES.support,
            ],
            tippyContent: t("usersTippy"),
        },
        {
            accessor: "options",
            name: t("options"),
            icon: "cogs",
            subTabs: [
                {
                    name: t("alerts"),
                    icon: "exclamation-triangle",
                    path: "/options/alerts",
                    roles: [ROLES.partnerAdmin, ROLES.support],
                },
                {
                    name: t("materialGroups"),
                    icon: "layer-group",
                    path: "/options/material-groups",
                    tippyContent: t("materialGroupsTippy"),
                },
                {
                    name: t("unitsManagement"),
                    icon: "percentage",
                    path: "/options/units-management",
                    roles: [ROLES.superAdmin],
                },
                {
                    name: t("classifiersManagement"),
                    icon: "object-group",
                    path: "/options/classifiers-management",
                    roles: [ROLES.support],
                },
                {
                    name: t("materialSuppliers"),
                    icon: "box-open",
                    path: "/options/materials-suppliers",
                    roles: [ROLES.partnerAdmin, ROLES.support],
                    hide: !user?.organization?.suppliersAccess,
                },
                {
                    name: t("lotManagement"),
                    icon: "map-marker-alt",
                    path: "/options/lots",
                    tippyContent: t("lotManagementTippy"),
                },
                {
                    name: t("deviceManagement"),
                    icon: "microchip",
                    path: "/options/device-management",
                    roles: [ROLES.partnerAdmin, ROLES.support],
                },
                {
                    name: t("publishedProducts"),
                    icon: "cloud",
                    path: "/published-products",
                    roles: [ROLES.partnerAdmin, ROLES.support],
                    hide: !isLabstoreAuthorized(user, LABSTORE_ACCESS.publish),
                },
                {
                    name: t("coupons"),
                    icon: "ticket-alt",
                    path: "/coupons",
                    roles: [ROLES.partnerAdmin, ROLES.support],
                    hide: !user?.organization?.createCouponAccess,
                },
                {
                    name: t("activityFeed"),
                    icon: "rss",
                    path: "/options/activity-feed",
                    roles: [ROLES.superAdmin, ROLES.support],
                    tippyContent: t("activityFeedTippy"),
                },
            ],
        },
        // {
        //     name: "Revenue",
        //     icon: "money-bill-wave",
        //     path: "/revenue",
        // },
        // {
        //     name: "Analytics",
        //     icon: "chart-line",
        //     path: "/analytics",
        // },
    ];

    const bottomTabs = [
        // {
        //     name: "Settings",
        //     icon: "cog",
        //     path: "/settings",
        // },
        {
            name: t("helpAndSupportSideMenu"),
            icon: "question",
            path: "/support",
            // callback: (event) => {
            //     window.location = "mailto:support@si-ware.com";
            //     event.preventDefault();
            // },
        },
    ];

    const handleCollapseTabs = (name) => {
        setCollapseTabs({ ...collapseTabs, [name]: !collapseTabs[name] });
    };

    const SideMenuWrapper = ({ tippyContent, children }) => {
        if (tippyContent) {
            return (
                <Tippy
                    content={tippyContent}
                    placement="right"
                    animation="scale"
                >
                    {children}
                </Tippy>
            );
        } else {
            return children;
        }
    };

    const renderSubTabItem = ({ tab, index }) => {
        return (
            <div className="collapsed-link" key={index}>
                <SideMenuWrapper tippyContent={tab.tippyContent}>
                    <li
                        className={`flex flex-col ${isActive(tab.path)}`}
                        onClick={() => handleCollapseTabs(tab.accessor)}
                        id={`side-menu-tab-${index}`}
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <i className={`fas fa-${tab.icon}`} />
                                <span className="ml-2-5">{tab.name}</span>
                            </div>
                            <div
                                className={`fas fa-chevron-right collapsed-icon text-xxs ${
                                    collapseTabs[tab.accessor] ? "rotated" : ""
                                }`}
                            ></div>
                        </div>
                    </li>
                </SideMenuWrapper>

                <ul
                    className={`sub-sidebar-menu ${
                        collapseTabs[tab.accessor] ? "show" : ""
                    }`}
                >
                    {tab.subTabs?.map((subTab, subIndex) => {
                        if (
                            (!subTab.roles ||
                                isAuthorized(subTab.roles, roles)) &&
                            !subTab.hide
                        ) {
                            return (
                                <SideMenuWrapper
                                    key={`sub-tab-${subIndex}`}
                                    tippyContent={subTab.tippyContent}
                                >
                                    <li
                                        className={`flex items-center ${isActive(
                                            subTab.path
                                        )}`}
                                        onClick={() =>
                                            closeSideMenu(subTab.path)
                                        }
                                        id={`side-menu-tab-${index}-subtab-${subIndex}`}
                                    >
                                        <i
                                            className={`fas fa-${subTab.icon}`}
                                        ></i>
                                        <Link
                                            className="text-left"
                                            to={subTab.path}
                                        >
                                            {subTab.name}
                                        </Link>
                                    </li>
                                </SideMenuWrapper>
                            );
                        }
                    })}
                </ul>
            </div>
        );
    };

    const renderTopItems = () => {
        return (
            topTabs &&
            topTabs.map((tab, index) => {
                if (
                    (!tab.roles || isAuthorized(tab.roles, roles)) &&
                    !tab.hide
                ) {
                    if (tab.subTabs) {
                        return renderSubTabItem({ tab, index });
                    }
                    return (
                        <SideMenuWrapper
                            key={index}
                            tippyContent={tab.tippyContent}
                        >
                            <li
                                className={`flex items-center ${isActive(
                                    tab.path
                                )}`}
                                onClick={() => closeSideMenu(tab.path)}
                                id={`side-menu-tab-${index}`}
                            >
                                <i className={`fas fa-${tab.icon}`} />
                                <Link to={tab.path}>{tab.name}</Link>
                            </li>
                        </SideMenuWrapper>
                    );
                }
            })
        );
    };

    const renderBottomItems = () => {
        return (
            bottomTabs &&
            bottomTabs.map((tab, index) => {
                if (!tab.hide) {
                    return (
                        <SideMenuWrapper
                            key={index}
                            tippyContent={tab.tippyContent}
                        >
                            <li
                                className={`flex items-center ${isActive(
                                    tab.path
                                )}`}
                                onClick={() => closeSideMenu(tab.path)}
                            >
                                <i className={`fas fa-${tab.icon}`} />
                                <Link to={tab.path} onClick={tab.callback}>
                                    {tab.name}
                                </Link>
                            </li>
                        </SideMenuWrapper>
                    );
                }
            })
        );
    };

    return (
        <>
            <aside
                ref={menuRef}
                className={`main-sidebar ${
                    fullMenu ? "uncollapse" : "collapse"
                } ${sideMenuOpen ? "show" : ""}`}
                id="main-sidebar"
            >
                <div
                    className="toggle-menu-btn toggle-menu-full"
                    data-element="main-sidebar"
                    onClick={toggleFullMenu}
                >
                    <i
                        className={`fas fa-chevron-${
                            fullMenu ? "left" : "right"
                        }`}
                    />
                </div>
                <div
                    className="toggle-menu-btn toggle-menu-mobile"
                    data-element="main-sidebar"
                    onClick={toggleSideMenu}
                >
                    <i
                        className={
                            "fas fa-cog " + (sideMenuOpen ? "fa-spin" : "")
                        }
                    />
                </div>
                <div className="flex flex-col justify-between h-full small-scroll-y overflow-auto overflow-x-hidden">
                    <ul className="sidebar-menu--left-list">
                        {renderTopItems()}
                    </ul>
                    <div className="sidebar-menu-left-footer">
                        <ul className="sidebar-menu-left-helper">
                            {renderBottomItems()}
                        </ul>
                    </div>
                </div>
            </aside>
        </>
    );
};

export default SideMenu;
